// Need math (doc 10 4.1, doc 43 2.2). Pure, no I/O.
// Units: values are centipoints 0..10000, rates are centipoints per REAL minute.
// Game time scale only matters to callers that convert (TIME_SCALE = 12); needs run on real time.

export const NEED_MAX = 10000;
export const NEED_IDS = ['hunger', 'energy', 'hygiene', 'fun', 'social'];

/** Activity tags a player can have at once. */
export const ACTIVITY_TAGS = ['idle', 'working', 'frying', 'sleeping', 'dancing', 'inCrowd', 'aloneAtHome', 'outageHeat', 'eating'];

/**
 * baseDecayPerMin is on the 0..100 scale (points per real minute).
 * Hunger is 100/180 so a full bar empties in exactly 180 min (doc table says 0.55, "~180 min").
 * restore: tag -> points per real minute gained (replaces decay while that tag is active).
 */
export const NEED_DEFS = {
    hunger: {
        id: 'hunger', enabled: true, baseDecayPerMin: 100 / 180,
        multipliers: { working: 1.25, sleeping: 0.25 },
        restore: {},
        lowMoodlets: [{ below: 30, moodletId: 'hungry' }, { below: 10, moodletId: 'starving' }],
        offline: { kind: 'converge', target: 50, tauHours: 3 },
    },
    energy: {
        id: 'energy', enabled: true, baseDecayPerMin: 0.4,
        multipliers: { working: 1.5, dancing: 2.0 },
        restore: { sleeping: 12 }, // foam mattress; spring bed 16 is an object override (S1-17)
        lowMoodlets: [{ below: 25, moodletId: 'tired' }, { below: 10, moodletId: 'exhausted' }],
        offline: { kind: 'converge', target: 70, tauHours: 2 },
    },
    hygiene: {
        id: 'hygiene', enabled: true, baseDecayPerMin: 0.5,
        multipliers: { frying: 2.0, dancing: 1.5, outageHeat: 1.25, sleeping: 0.3 },
        restore: {},
        lowMoodlets: [{ below: 15, moodletId: 'smelling' }],
        offline: { kind: 'converge', target: 50, tauHours: 3 },
    },
    fun: {
        id: 'fun', enabled: false, baseDecayPerMin: 0.55,
        multipliers: { working: 1.5, sleeping: 0 },
        restore: {},
        lowMoodlets: [{ below: 25, moodletId: 'bored' }],
        offline: { kind: 'converge', target: 50, tauHours: 3 },
    },
    social: {
        id: 'social', enabled: false, baseDecayPerMin: 0.5,
        multipliers: { inCrowd: 0.5, sleeping: 0, aloneAtHome: 1.25 },
        restore: {},
        lowMoodlets: [{ below: 25, moodletId: 'lonely' }],
        offline: { kind: 'converge', target: 50, tauHours: 3 },
    },
};

export const ENABLED_NEED_IDS = NEED_IDS.filter((id) => NEED_DEFS[id].enabled);

/** Zero rules (simplified 10 4.3). */
export const ZERO_RULES = {
    hungerRescueAfterMs: 10 * 60_000,
    hungerRescueValue: 3500,
    energyCollapseValue: 2000,
};

const clamp = (x) => Math.min(NEED_MAX, Math.max(0, x));

/** Anchor: { v, r, t0 } -> value at t0, rate (centipoints/min, signed), server ms. */
export function makeAnchor(v, r, t0) {
    return { v: clamp(v), r, t0 };
}

/** value(t) = clamp(v + r * (t - t0) / 60000, 0, 10000). Returns centipoints. */
export function needValueAt(anchor, nowMs) {
    return clamp(anchor.v + (anchor.r * (nowMs - anchor.t0)) / 60_000);
}

/** Signed centipoints per real minute for a need under a set of active tags. */
export function needRate(needId, tags) {
    const def = NEED_DEFS[needId];
    if (!def.enabled) return 0;
    const set = tags instanceof Set ? tags : new Set(tags);
    for (const tag of set) {
        if (def.restore[tag] != null) return def.restore[tag] * 100;
    }
    let mult = 1;
    for (const tag of set) if (def.multipliers[tag] != null) mult *= def.multipliers[tag];
    return -def.baseDecayPerMin * mult * 100;
}

/** Ms until the anchor reaches 0 (Infinity if not decaying), or to full if rising. */
export function timeToBoundMs(anchor, nowMs) {
    if (anchor.r === 0) return Infinity;
    const v = needValueAt(anchor, nowMs);
    const target = anchor.r < 0 ? 0 : NEED_MAX;
    return ((target - v) / anchor.r) * 60_000;
}
